import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Box,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Collapse,
  IconButton,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import BuildIcon from '@mui/icons-material/Build';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BarChartIcon from '@mui/icons-material/BarChart';
import SettingsIcon from '@mui/icons-material/Settings';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ListIcon from '@mui/icons-material/List';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import theme from './theme';

import Home from './components/Home';
import CampaignTracker from './components/CampaignTracker';
import Analytics from './components/Analytics';
import ChannelKPIs from './components/ChannelKPIs';
import KPITimeseries from './components/KPITimeseries';
import ConfigSettings from './components/ConfigSettings';
import CampaignCompleteness from './components/CampaignCompleteness';
import CampaignsList from './components/CampaignsList';
import Logo from './components/Logo';
import Login from './components/Login';
import { apiService } from './services/apiService';
import authService from './services/authService';

// Tema personalizado importado desde theme.js

// Singleton module-level: evita que StrictMode (double-invoke en dev) procese
// dos veces el authorization code del callback OIDC, lo cual genera redirects
// duplicados a Authentik (cada code es one-time-use).
let callbackPromise = null;

const DRAWER_WIDTH = 280;
const DRAWER_WIDTH_MOBILE = 260;

function App() {
  const theme_mui = useTheme();
  const isMobile = useMediaQuery(theme_mui.breakpoints.down('md'));
  
  const [currentView, setCurrentView] = useState('home');
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [campaignTrackingOpen, setCampaignTrackingOpen] = useState(false);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [user, setUser] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);
  const [autoOpenNewToolbox, setAutoOpenNewToolbox] = useState(false);
  const [homeRefreshKey, setHomeRefreshKey] = useState(0);

  useEffect(() => {
    // Ajustar drawer según tamaño de pantalla
    setDrawerOpen(!isMobile);
  }, [isMobile]);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      setLoading(true);

      // Si venimos del callback de Authentik (?code=...)
      if (window.location.search.includes('code=') || window.location.pathname === '/callback') {
        try {
          if (!callbackPromise) {
            callbackPromise = authService.handleCallback().finally(() => {
              window.history.replaceState({}, '', '/');
            });
          }
          await callbackPromise;
        } catch (err) {
          console.error('OIDC callback error:', err);
          callbackPromise = null;
          await authService.login();
          return;
        }
      }

      const oidcUser = await authService.getUser();
      if (oidcUser && !oidcUser.expired) {
        try {
          const { user: backendUser } = await apiService.verifyAuth();
          setUser(backendUser);
          setIsAuthenticated(true);
        } catch (err) {
          // 403: autenticado en Authentik pero sin acceso a esta app (usuario no provisionado en DB)
          // No re-loguear (evita loop). Mostrar pantalla de acceso denegado con opción a logout.
          if (err.response?.status === 403) {
            setAccessDenied(true);
            setLoading(false);
            return;
          }
          // 401 u otros: el interceptor ya disparó login(); aquí solo salimos.
          return;
        }
      } else {
        // Sin sesión activa → mostrar pantalla de login
        setLoading(false);
        return;
      }

      setLoading(false);
    } catch (err) {
      setError('Error al verificar autenticación');
      setLoading(false);
    }
  };



  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleViewChange = (view) => {
    setCurrentView(view);
    if (view === 'home') {
      setHomeRefreshKey((k) => k + 1);
    }
    // Si cambiamos de vista, limpiar la campaña seleccionada excepto si vamos al tracker
    if (view !== 'campaign-tracker') {
      setSelectedCampaignId(null);
    }
    // Cerrar drawer en móvil después de seleccionar
    if (isMobile) {
      setDrawerOpen(false);
    }
  };

  const handleViewCampaign = (campaignId) => {
    setSelectedCampaignId(campaignId);
    setCurrentView('campaign-tracker');
    // Abrir drawer si está cerrado
    if (!drawerOpen) {
      setDrawerOpen(true);
    }
  };

  const handleToggleSection = (section) => {
    if (section === 'campaign-tracking') {
      setCampaignTrackingOpen(!campaignTrackingOpen);
    } else if (section === 'analytics') {
      setAnalyticsOpen(!analyticsOpen);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleLogout = () => {
    setAnchorEl(null);
    authService.logout();
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  if (error) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Container>
          <Alert severity="error" sx={{ mt: 4 }}>
            {error}
          </Alert>
        </Container>
      </ThemeProvider>
    );
  }

  if (accessDenied) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Container maxWidth="sm">
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="100vh" gap={3}>
            <Alert severity="warning" sx={{ width: '100%' }}>
              Tu cuenta está autenticada pero no tiene acceso a Marketing Tracker. Contacta al administrador para solicitar acceso.
            </Alert>
            <Button variant="contained" onClick={() => authService.logout()}>
              Cerrar sesión
            </Button>
          </Box>
        </Container>
      </ThemeProvider>
    );
  }

  // Sin sesión → pantalla de login con botón explícito de SSO
  if (!isAuthenticated) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Login />
      </ThemeProvider>
    );
  }

  // Mapeo de vistas a componentes
  const renderView = () => {
    const viewComponents = {
      'home': <Home showSnackbar={showSnackbar} isActive={currentView === 'home'} homeRefreshKey={homeRefreshKey} />,
      'campaigns-list': <CampaignsList showSnackbar={showSnackbar} onViewCampaign={handleViewCampaign} onCreateNew={() => { setAutoOpenNewToolbox(true); handleViewChange('campaign-tracker'); setTimeout(() => setAutoOpenNewToolbox(false), 200); }} />,
      'campaign-tracker': <CampaignTracker showSnackbar={showSnackbar} selectedCampaignId={selectedCampaignId} onCampaignSelected={() => setSelectedCampaignId(null)} autoOpenNew={autoOpenNewToolbox} />,
      'campaign-completeness': <CampaignCompleteness />,
      'analytics': <Analytics showSnackbar={showSnackbar} />,
      'kpi-timeseries': <KPITimeseries showSnackbar={showSnackbar} />,
      'channel-kpis': <ChannelKPIs showSnackbar={showSnackbar} />,
      'config': <ConfigSettings showSnackbar={showSnackbar} />
    };
    return viewComponents[currentView] || viewComponents['home'];
  };

  // Contenido del Drawer
  const drawerContent = (
    <Box>
      <Toolbar sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 64, px: { xs: 1, sm: 2 } }}>
        <Logo size="small" />
        <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ ml: 1, fontWeight: 600 }}>
          Marketing Tracker
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {/* Home */}
        <ListItem disablePadding>
          <ListItemButton 
            selected={currentView === 'home'}
            onClick={() => handleViewChange('home')}
          >
            <ListItemIcon>
              <HomeIcon color={currentView === 'home' ? 'primary' : 'inherit'} />
            </ListItemIcon>
            <ListItemText primary="Home" />
          </ListItemButton>
        </ListItem>
        
        <Divider sx={{ my: 1 }} />
        
        {/* Seguimiento de ToolBox */}
        <ListItem disablePadding>
          <ListItemButton onClick={() => handleToggleSection('campaign-tracking')}>
            <ListItemIcon>
              <AssignmentIcon />
            </ListItemIcon>
            <ListItemText primary="Seguimiento de ToolBox" />
            {campaignTrackingOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={campaignTrackingOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemButton 
              sx={{ pl: 4 }}
              selected={currentView === 'campaigns-list'}
              onClick={() => handleViewChange('campaigns-list')}
            >
              <ListItemIcon>
                <ListIcon color={currentView === 'campaigns-list' ? 'primary' : 'inherit'} />
              </ListItemIcon>
              <ListItemText primary="Todos los ToolBox" />
            </ListItemButton>
            <ListItemButton 
              sx={{ pl: 4 }}
              selected={currentView === 'campaign-tracker'}
              onClick={() => handleViewChange('campaign-tracker')}
            >
              <ListItemIcon>
                <BuildIcon color={currentView === 'campaign-tracker' ? 'primary' : 'inherit'} />
              </ListItemIcon>
              <ListItemText primary="Tracker de ToolBox" />
            </ListItemButton>
            <ListItemButton 
              sx={{ pl: 4 }}
              selected={currentView === 'analytics'}
              onClick={() => handleViewChange('analytics')}
            >
              <ListItemIcon>
                <AnalyticsIcon color={currentView === 'analytics' ? 'primary' : 'inherit'} />
              </ListItemIcon>
              <ListItemText primary="Analytics" />
            </ListItemButton>
            <ListItemButton 
              sx={{ pl: 4 }}
              selected={currentView === 'campaign-completeness'}
              onClick={() => handleViewChange('campaign-completeness')}
            >
              <ListItemIcon>
                <HelpOutlineIcon color={currentView === 'campaign-completeness' ? 'primary' : 'inherit'} />
              </ListItemIcon>
              <ListItemText primary="Completitud" />
            </ListItemButton>
          </List>
        </Collapse>
        
        <Divider sx={{ my: 1 }} />
        
        {/* Analítica de Redes */}
        <ListItem disablePadding>
          <ListItemButton onClick={() => handleToggleSection('analytics')}>
            <ListItemIcon>
              <BarChartIcon />
            </ListItemIcon>
            <ListItemText primary="Analítica de Redes" />
            {analyticsOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={analyticsOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <ListItemButton 
              sx={{ pl: 4 }}
              selected={currentView === 'kpi-timeseries'}
              onClick={() => handleViewChange('kpi-timeseries')}
            >
              <ListItemIcon>
                <ShowChartIcon color={currentView === 'kpi-timeseries' ? 'primary' : 'inherit'} />
              </ListItemIcon>
              <ListItemText primary="Timeseries KPIs" />
            </ListItemButton>
            <ListItemButton 
              sx={{ pl: 4 }}
              selected={currentView === 'channel-kpis'}
              onClick={() => handleViewChange('channel-kpis')}
            >
              <ListItemIcon>
                <ShowChartIcon color={currentView === 'channel-kpis' ? 'primary' : 'inherit'} />
              </ListItemIcon>
              <ListItemText primary="KPIs por Canal" />
            </ListItemButton>
          </List>
        </Collapse>
        
        <Divider sx={{ my: 1 }} />
        
        {/* Configuración */}
        <ListItem disablePadding>
          <ListItemButton 
            selected={currentView === 'config'}
            onClick={() => handleViewChange('config')}
          >
            <ListItemIcon>
              <SettingsIcon color={currentView === 'config' ? 'primary' : 'inherit'} />
            </ListItemIcon>
            <ListItemText primary="Configuración" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ display: 'flex', width: '100%' }}>
        <AppBar 
          position="fixed" 
          sx={{ 
            zIndex: (theme) => theme.zIndex.drawer + 1,
            width: '100%',
            left: 0,
            right: 0,
            borderRadius: 0,
            boxShadow: 2,
          }}
        >
          <Toolbar sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
              {currentView !== 'home' && (
                <IconButton
                  color="inherit"
                  aria-label="volver al inicio"
                  onClick={() => handleViewChange('home')}
                  sx={{ mr: 1 }}
                >
                  <ArrowBackIcon />
                </IconButton>
              )}
              <Logo size={isMobile ? 'small' : 'medium'} />
              <Typography 
                variant={isMobile ? 'subtitle1' : 'h6'} 
                component="div" 
                sx={{ fontWeight: 600, ml: { xs: 1, sm: 2 }, display: { xs: 'none', sm: 'block' } }}
              >
                Marketing Tracker
              </Typography>
            </Box>
            
            {/* Menú de usuario */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ mr: 1, display: { xs: 'none', md: 'block' } }}>
                {user?.full_name}
              </Typography>
              <Button
                color="inherit"
                onClick={handleMenuOpen}
                startIcon={<AccountCircleIcon />}
                sx={{ textTransform: 'none', minWidth: { xs: 'auto', sm: '120px' } }}
              >
                <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                  {user?.username}
                </Box>
              </Button>
              <IconButton
                color="inherit"
                aria-label="abrir menú"
                edge="end"
                onClick={handleDrawerToggle}
                sx={{ ml: { xs: 0.5, sm: 2 }, minWidth: 44, minHeight: 44 }}
              >
                <MenuIcon />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <MenuItem onClick={handleMenuClose}>
                  <Typography variant="body2">
                    <strong>Usuario:</strong> {user?.username}
                  </Typography>
                </MenuItem>
                <MenuItem onClick={handleMenuClose}>
                  <Typography variant="body2">
                    <strong>Rol:</strong> {user?.role}
                  </Typography>
                </MenuItem>
                <MenuItem onClick={handleMenuClose}>
                  <Typography variant="body2">
                    <strong>Región:</strong> {
                      user?.region_access === 'all' ? 'Todas las regiones' :
                      user?.region_access === 'cono_sur' ? 'Cono Sur' :
                      user?.region_access === 'centro_america' ? 'Centro América + México' :
                      user?.region_access
                    }
                  </Typography>
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                  <LogoutIcon sx={{ mr: 1 }} />
                  Cerrar Sesión
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Contenido Principal */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            flexShrink: 1,
            p: { xs: 1.5, sm: 2, md: 3 },
            width: drawerOpen && !isMobile ? `calc(100vw - ${DRAWER_WIDTH}px)` : '100%',
            minWidth: 0,
            transition: theme.transitions.create(['width'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
            overflow: 'auto',
          }}
        >
          <Toolbar sx={{ minHeight: { xs: '56px', sm: '64px' } }} /> {/* Espacio para AppBar */}
          <Container 
            maxWidth="xl"
            sx={{
              width: '100%',
              px: { xs: 1, sm: 2, md: 3 },
              mt: 0,
            }}
          >
            {renderView()}
          </Container>
        </Box>

        {/* Drawer */}
        <Drawer
          variant={isMobile ? 'temporary' : 'persistent'}
          anchor="right"
          open={drawerOpen}
          onClose={handleDrawerToggle}
          sx={{
            width: drawerOpen ? (isMobile ? DRAWER_WIDTH_MOBILE : DRAWER_WIDTH) : 0,
            flexShrink: 0,
            zIndex: (theme) => theme.zIndex.drawer,
            '& .MuiDrawer-paper': {
              width: isMobile ? DRAWER_WIDTH_MOBILE : DRAWER_WIDTH,
              boxSizing: 'border-box',
              borderRadius: 0,
              borderLeft: '1px solid rgba(0, 0, 0, 0.12)',
            },
          }}
        >
          {drawerContent}
        </Drawer>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
}

export default App;
